import { type FC, useState } from 'react';

import { detectOverflow, type State as PopperState } from '@popperjs/core';
import { pick } from 'lodash-es';

import { FormControl } from '@craft/ui/components/forms';
import { InputLabel, Select } from '@craft/ui/components/input';
import { Dropdown, type DropdownProps, GenericMenuItem } from '@craft/ui/components/lists';
import { Box } from '@craft/ui/components/structural';
import { quantitativeSizeToString } from '@craft/ui/util';

import { type CollaborativeRap } from '../../types';
import { CompetencyTaggingList } from '../lists/CompetencyTaggingList';

import {
  CompetencyTaggingDropdownHelperText,
  selectedRapHasTags,
} from './CompetencyTaggingDropdownHelperText';

const DefaultPopperWidth = 320;

export const constrainWidthToRightViewportEdge = (state: PopperState) => {
  const constrainableElement = state.elements.popper.firstChild;
  if (constrainableElement instanceof HTMLElement) {
    const { right } = detectOverflow(state);
    if (right > 0) {
      const availableWidth = Math.max(state.rects.popper.width - right, 0);
      const widthConstraint = [
        'min(',
        [
          quantitativeSizeToString(availableWidth, 'px'),
          quantitativeSizeToString(DefaultPopperWidth, 'px'),
        ].join(', '),
        ')',
      ].join('');
      constrainableElement.style.maxWidth = widthConstraint;
      constrainableElement.style.minWidth = widthConstraint;
    }
  }
};

interface CompetencyTaggingDropdownProps extends Omit<
  DropdownProps,
  'content' | 'maxWidth' | 'minWidth' | 'onOpen' | 'width'
> {
  readonly collaborativeRaps: CollaborativeRap[];
  readonly onCompetencyTagToggle: (tagId: string) => void;
  readonly selectedCompetencyTagIds: string[];
}

export const CompetencyTaggingDropdown: FC<CompetencyTaggingDropdownProps> = ({
  children,
  collaborativeRaps,
  onCompetencyTagToggle,
  selectedCompetencyTagIds,
  ...props
}) => {
  const [selectedRapId, setSelectedRapId] = useState<null | string>(null);
  const selectedRap = collaborativeRaps.find(rap => rap.id === selectedRapId) ?? null;

  return (
    <Dropdown
      {...props}
      content={({ props: contentProps }) => (
        <>
          <Box
            paddingBottom={selectedRap && selectedRapHasTags(selectedRap) ? 1 : 2}
            paddingTop={2}
            paddingX={1}
          >
            <FormControl fullWidth>
              <InputLabel id='rap-label'>Select a RAP</InputLabel>
              <Select
                label='Select a RAP'
                labelId='rap-label'
                onChange={e => setSelectedRapId(e.target.value || null)}
                value={selectedRapId ?? ''}
              >
                {collaborativeRaps.map(rap => (
                  <GenericMenuItem
                    key={rap.id}
                    lineClamp={2}
                    titleProps={{ title: rap.name }}
                    value={rap.id}
                    whiteSpace='pre-line'
                  >
                    {rap.name}
                  </GenericMenuItem>
                ))}
              </Select>
              <CompetencyTaggingDropdownHelperText selectedRap={selectedRap} />
            </FormControl>
          </Box>
          {selectedRap && selectedRapHasTags(selectedRap) && (
            <CompetencyTaggingList
              {...contentProps}
              competencyTagsData={pick(selectedRap, [
                'competencyTagGroups',
                'ungroupedCompetencyTags',
              ])}
              onTagToggle={onCompetencyTagToggle}
              selectedTagIds={selectedCompetencyTagIds}
            />
          )}
        </>
      )}
      maxWidth={quantitativeSizeToString(DefaultPopperWidth, 'px')}
      minWidth={quantitativeSizeToString(DefaultPopperWidth, 'px')}
      modifiers={[
        {
          name: 'preventOverflow',
          options: {
            boundary: 'viewport',
            padding: 10,
          },
        },
        {
          fn({ state }) {
            return constrainWidthToRightViewportEdge(state);
          },
          name: 'constrainWidthToRightViewportEdge',
          phase: 'main',
        },
      ]}
      offset={[0, 10]}
      onOpen={() => setSelectedRapId(null)}
      placement='bottom-start'
    >
      {children}
    </Dropdown>
  );
};
